/**
 * Map utility functions for geographic calculations, cluster operations, and map management
 */

import maplibregl from 'maplibre-gl';

/**
 * Calculate the geographic midpoint of a collection of coordinates
 * @param {Array} coords - Array of coordinate objects with latitude and longitude properties
 * @returns {Object} - Object containing latitude and longitude of the midpoint
 */
export function geographicMidpoint(coords) {
  let x = 0,
    y = 0,
    z = 0;

  coords.forEach(({ latitude, longitude }) => {
    const lat = (latitude * Math.PI) / 180;
    const lon = (longitude * Math.PI) / 180;
    x += Math.cos(lat) * Math.cos(lon);
    y += Math.cos(lat) * Math.sin(lon);
    z += Math.sin(lat);
  });

  const total = coords.length;
  x /= total;
  y /= total;
  z /= total;

  const lon = Math.atan2(y, x);
  const hyp = Math.sqrt(x * x + y * y);
  const lat = Math.atan2(z, hyp);

  return {
    latitude: (lat * 180) / Math.PI,
    longitude: (lon * 180) / Math.PI,
  };
}

/**
 * Get cluster items from a MapLibre cluster source
 * @param {Object} mapSource - MapLibre cluster source
 * @param {number} clusterId - ID of the cluster
 * @param {number} pointCount - Number of points in the cluster
 * @returns {Promise<Array>} - Promise resolving to array of cluster items
 */
export async function getClusterItems(mapSource, clusterId, pointCount) {
  const features = await mapSource.getClusterLeaves(clusterId, pointCount);

  return features.map((leaf) => {
    const p = leaf.properties || {};
    const [longitude, latitude] = leaf.geometry?.coordinates || [];

    return {
      longitude,
      latitude,
      id: p.id,
      price: p.price,
      name: p.name,
      typeString: p.typeString,
      advertType: p.advertType,
      images: p.images,
      roomsAvailableCount: p.roomsAvailableCount,
      numberOfBathRooms: p.numberOfBathRooms,
      spareRoomsAvailableCount: p.spareRoomsAvailableCount,
      travelData: parseTravelData(p.travelData),
      url: p.url,
      isShortlisted: p.isShortlisted,
      featured: p.featured,
    };
  });
}

/**
 * Check if coordinates are within map bounds
 * @param {Array} coord - Array of [longitude, latitude]
 * @param {Object} bounds - MapLibre bounds object
 * @returns {boolean} - True if coordinates are within bounds
 */
export function isCoordinateInBounds(coord, bounds) {
  return (
    coord[0] >= bounds.getWest() &&
    coord[0] <= bounds.getEast() &&
    coord[1] >= bounds.getSouth() &&
    coord[1] <= bounds.getNorth()
  );
}

/**
 * Parse travel data from string or return as-is
 * @param {string|Array} travelData - Travel data that might be a JSON string
 * @returns {Array} - Parsed travel data array
 */
export function parseTravelData(travelData) {
  if (typeof travelData === 'string') {
    try {
      return JSON.parse(travelData);
    } catch (e) {
      console.warn('Failed to parse travelData:', e);
      return [];
    }
  }
  return travelData || [];
}

/**
 * Clear all markers from a marker array
 * @param {Array} markerElements - Array of MapLibre marker elements
 */
export function clearMarkers(markerElements) {
  markerElements.forEach((marker) => {
    marker.remove();
  });
  markerElements.length = 0; // Clear the array
}

/**
 * Create map options object for MapLibre initialization
 * @param {Object} config - Map configuration object
 * @returns {Object} - MapLibre map options
 */
export function createMapOptions(config) {
  const mapOptions = {
    container: config.container,
    style: config.style,
    center: config.center,
    zoom: config.zoom,
    minZoom: config.minZoom,
    maxZoom: config.maxZoom,
  };

  // Add maxBounds if provided
  if (config.maxBounds) {
    mapOptions.maxBounds = config.maxBounds;
  }

  return mapOptions;
}

/**
 * Add cluster source and layers to a MapLibre map
 * @param {Object} map - MapLibre map instance
 * @param {string} sourceId - Source ID for the cluster source
 */
export function addClusterSourceAndLayers(map, sourceId = 'properties') {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 20,
      clusterRadius: 40,
    });
  }

  if (!map.getLayer('clusters')) {
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#93c5fd',
          10,
          '#60a5fa',
          25,
          '#3b82f6',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 25, 24],
        'circle-opacity': 0, // makes the default cluster invisible
      },
    });
  }

  if (!map.getLayer('unclustered-point')) {
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 10,
        'circle-opacity': 0, // makes the default cluster invisible
      },
    });
  }
}

/**
 * Update cluster source data with properties
 * @param {Object} map - MapLibre map instance
 * @param {Array} properties - Array of property objects
 * @param {string} sourceId - Source ID for the cluster source
 */
export function updateClusterSource(map, properties, sourceId = 'properties') {
  const features = properties.map((m) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [m.longitude, m.latitude] },
    properties: {
      id: m.id,
      price: m.price,
      name: m.name,
      typeString: m.typeString,
      advertType: m.advertType,
      images: m.images,
      roomsAvailableCount: m.bedrooms,
      numberOfBathRooms: m.bathrooms,
      spareRoomsAvailableCount: m.spareRoomsAvailableCount,
      travelData: m.travelData,
      url: m.url,
      isShortlisted: m.isShortlisted,
      featured: m.featured,
    },
  }));

  const fc = { type: 'FeatureCollection', features };
  const src = map.getSource(sourceId);
  if (src) src.setData(fc);
}

/**
 * Style MapLibre navigation control
 * @param {HTMLElement} container - Map container element
 * @param isSinglePropertyMapPage
 */
export function styleNavigationControl(
  container,
  isSinglePropertyMapPage = false
) {
  const navControl = container.querySelector('.maplibregl-ctrl-group');
  if (!navControl) return;

  const cleanupFunctions = [];

  let navControlStyle = `
    background: white !important;
    border-radius: 20px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    border: none !important;
    padding: 4px !important;
    width: 40px !important;
    height: 80px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
`;

  // If isSinglePropertyMapPage is false and page width is < 768px, add a margin top to the navigation control
  if (!isSinglePropertyMapPage && window.innerWidth < 768) {
    navControlStyle += 'margin-top: 75px !important;';
  }

  // Apply custom styling to match the design
  navControl.style.cssText = navControlStyle;

  // Style the zoom buttons
  const zoomButtons = navControl.querySelectorAll(
    '.maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out'
  );
  zoomButtons.forEach((button) => {
    button.style.cssText = `
      background: transparent !important;
      border: none !important;
      width: 32px !important;
      height: 32px !important;
      border-radius: 16px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      color: #3b82f6 !important;
      margin: 0 !important;
      -webkit-tap-highlight-color: transparent !important;
    `;

    const pressStart = () => {
      button.style.transform = 'scale(0.9)';
      button.style.backgroundColor = '#e0f2fe';
    };

    const pressEnd = () => {
      button.style.transform = 'scale(1)';
      button.style.backgroundColor = 'transparent';
    };

    button.addEventListener('mousedown', pressStart);
    button.addEventListener('mouseup', pressEnd);
    button.addEventListener('mouseleave', pressEnd);

    // Mobile / touch events
    button.addEventListener('touchstart', pressStart, { passive: true });
    button.addEventListener('touchend', pressEnd, { passive: true });
    button.addEventListener('touchcancel', pressEnd, { passive: true });

    // Store cleanup function
    cleanupFunctions.push(() => {
      button.removeEventListener('mousedown', pressStart);
      button.removeEventListener('mouseup', pressEnd);
      button.removeEventListener('mouseleave', pressEnd);
      button.removeEventListener('touchstart', pressStart);
      button.removeEventListener('touchend', pressEnd);
      button.removeEventListener('touchcancel', pressEnd);
    });
  });

  // Add divider between buttons
  const zoomInButton = navControl.querySelector('.maplibregl-ctrl-zoom-in');
  const zoomOutButton = navControl.querySelector('.maplibregl-ctrl-zoom-out');

  if (zoomInButton && zoomOutButton) {
    const divider = document.createElement('div');
    divider.style.cssText = `
      width: 100% !important;
      height: 1px !important;
      background-color: #e5e7eb !important;
      margin: 4px 0 !important;
    `;
    zoomInButton.parentNode.insertBefore(divider, zoomOutButton);
  }

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
}

/**
 * Generic flyTo function for MapLibre maps
 * @param {Object} map - MapLibre map instance
 * @param {Object} options - FlyTo options
 * @param {Array} options.center - [longitude, latitude] coordinates
 * @param {number} [options.zoom] - Target zoom level
 * @param {number} [options.zoomOffset] - Zoom offset from current zoom (e.g., +1)
 * @param {boolean} [options.essential] - Whether animation is essential for accessibility
 * @param {number} [options.southOffset] - Base offset south to account for UI overlay (default: 0.001)
 * @param {boolean} [options.scaleOffsetWithZoom] - Whether to scale offset with zoom level (default: true)
 */
export function flyTo(map, options) {
  if (!map) return;

  const {
    center,
    zoom,
    zoomOffset = 0,
    essential = true,
    southOffset = 0.0015,
    scaleOffsetWithZoom = true,
  } = options;

  // Calculate the target zoom level, round then add a small margin to the calculated zoom to avoid issues where two layers of clusters are showing.
  let targetZoom;
  if (zoom !== undefined) {
    targetZoom = zoom;
  } else if (zoomOffset !== 0) {
    targetZoom = Math.round(map.getZoom()) + zoomOffset + 0.01;
  } else {
    targetZoom = Math.round(map.getZoom()) + 0.01;
  }

  // Scale the south offset with zoom level to account for UI overlay
  // Higher zoom = smaller offset needed, lower zoom = larger offset needed
  let scaledSouthOffset = southOffset;
  if (scaleOffsetWithZoom) {
    // Scale factor: 2^(11 - zoom) where 11 is our reference zoom level
    // This means at zoom 11, offset = baseOffset, at zoom 12, offset = 0.5x baseOffset, etc.
    const zoomScaleFactor = Math.pow(2, 11 - targetZoom);
    scaledSouthOffset = southOffset * zoomScaleFactor;
  }

  const flyToOptions = {
    center: [center[0], center[1] - scaledSouthOffset],
    essential,
  };

  // Add zoom to options if specified
  if (zoom !== undefined || zoomOffset !== 0) {
    flyToOptions.zoom = targetZoom;
  }

  map.flyTo(flyToOptions);
}

export function updateAttributionText(refs) {
  // Target the maplibregl-ctrl-attrib-inner class and replace text with OSM attribution
  const attributionInner = refs.maplibreContainer.querySelector(
    '.maplibregl-ctrl-attrib-inner'
  );
  if (attributionInner) {
    attributionInner.innerHTML =
      '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
  }
}

export function initMap(mapOptions) {
  const map = new maplibregl.Map(mapOptions);

  map.addControl(
    new maplibregl.NavigationControl({ showCompass: false }),
    'top-right'
  );

  return map;
}
